import { Check, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { CargoTypeValue, ServiceTag, TaskTimeModeValue, TaskTypeValue } from '@my/tms-api';
import {
  useCreateOrderBooking,
  useCreateOrderTask,
  useCreateOrderTaskCargoItem,
} from '@my/tms-api/hooks';
import type { Address } from '@my/directory-api';
import { useFerryCredentials } from '@my/ferry-api/hooks';
import { SelectInput, TextInput, showToast } from '@my/ui';
import { EMPTY_BOOKING_FORM_VALUES } from '../lib/bookingForm';
import {
  CARGO_TASK_TYPES,
  EMPTY_CARGO_ITEM_DRAFT,
  UNIT_IDENTIFIER_FAMILIES,
  cargoHandlingPhaseFor,
  cargoItemKindFor,
  cargoItemRowCountFor,
  resolveLoadingMetres,
  taskTypeOwnsCargoItems,
  taskTypeSupportsReference,
} from '../lib/cargoPick';
import { unitDraftForRow } from '../lib/cargoCascade';
import { deriveDeliveryItem, type AllocationMap } from '../lib/allocation';
import { toProductInputs } from '../lib/cargoProducts';
import { formatTempSpecRange, temperatureRangeViolation } from '../lib/format';
import { TASK_TYPE_LABELS_BE } from '../lib/taskTypeConfig';
import type {
  BookingFormValues,
  CargoFamily,
  CargoItemDraft,
  CollectionItemOption,
  CargoProductOption,
  PalletVariantOption,
  TaskTemperatureSpec,
  TimeMode,
} from '../lib/types';
import { AllocationTable } from './AllocationTable';
import { BookingFormFields } from './BookingFormFields';
import { IntegrationSwitch } from '../../ferry/components/IntegrationSwitch';
import { ferryBlockingMessage, useFerryBookingFlow } from '../../ferry/flow';
import { useShippingIntegration } from '../../ferry/shippingIntegration';
import { TaskLocationMap } from './BookingMap';
import { CargoItemInputsSection } from './CargoItemRow';
import { NewLocationButton } from '@/features/locations/NewLocationModal';
import {
  Combobox,
  InputWithSpecHint,
  NumberFieldInput,
  TextAreaInput,
  QuarterHourTimeInput,
  TimeModePicker,
  type ComboboxOption,
} from './inputs';

/** Local YYYY-MM-DD for the native date input — a new task defaults to today. */
function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** "HH:mm" for the current clock time rounded UP to the next quarter hour. */
function nextQuarterFromNow(): string {
  const now = new Date();
  const mins = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 15) * 15;
  if (mins >= 24 * 60) return '23:45';
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

/** Add minutes to an "HH:mm[:ss]" time, snapped to the nearest quarter hour, capped at 23:45. */
function addMinutesSnapped(hhmm: string, add: number): string {
  const [h, m] = hhmm.slice(0, 5).split(':').map(Number);
  let mins = Math.round(((h ?? 0) * 60 + (m ?? 0) + add) / 15) * 15;
  if (mins >= 24 * 60) return '23:45';
  if (mins < 0) mins = 0;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

/**
 * Default exact planned time when the operator switches to Exact with none set:
 *  • a previous task exists → 30 min after it (interim — distance-aware later);
 *  • else the date is today → the next quarter-hour from now;
 *  • else (future date) → 08:00.
 */
function defaultExactTime(previousTime: string | null, plannedDate: string): string {
  if (previousTime) return addMinutesSnapped(previousTime, 30);
  if (plannedDate === todayIso()) return nextQuarterFromNow();
  return '08:00';
}

interface TaskCreateFormProps {
  /** Required — POST target. The parent only renders this form once the order has an id. */
  orderId: string;
  /** The order's reference, which a ferry booking carries to the carrier as the customer reference. */
  orderNumber?: string | null;
  /** The order carries hazardous goods; a ferry booking is marked dangerous goods by default. */
  dangerousGoods?: boolean;
  /** The cargo definition's equipment types, catalogue keys; a ferry booking's vehicle type follows the first one mapped. */
  equipmentKeys?: string[];
  /**
   * Picked cargo type drives the legal task-type list (see
   * <c>CARGO_TASK_TYPES</c>). Required — the "+ Add" button is gated
   * on cargo so this never renders without one.
   */
  cargoType: CargoTypeValue;
  /** Coarse family of the cargo — drives the cargo-item input set. */
  cargoFamily: CargoFamily;
  /**
   * Pallet types for the Pallet Type picker (pallet family only). Sourced
   * from the picked cargo definition's variants. Empty for other families.
   */
  palletVariants: PalletVariantOption[];
  /** Products a collection row may carry; null hides the picker. */
  productOptions: CargoProductOption[] | null;
  /** The cargo type has Compartments — several products per unit. */
  multiProduct: boolean;
  /**
   * Temperature spec from the picked cargo definition, or null when the
   * cargo type isn't temperature-controlled. When present, the form
   * shows a per-task Temp box validated against this range — mirroring
   * the order-level box.
   */
  temperatureSpec: TaskTemperatureSpec | null;
  /**
   * Initial temperature for this task — the order-level value, falling
   * back to the preceding leg's when the header carries none (resolved by
   * the parent, which owns the task list). Null when the cargo is not
   * temperature-controlled or nothing is set anywhere.
   */
  defaultTemperatureC?: number | null;
  /**
   * Unit identifiers (BIC code / Tank ID) to pre-fill, keyed by 1-based
   * `itemIndex` — carried forward from the preceding leg, because it's
   * the same physical box or barrel arriving at the next stop. Empty for
   * families whose cargo item has no identity (pallet lines).
   */
  defaultUnitIdentifiers?: Map<number, string>;
  /**
   * 1 or 2 — number of physical units per trailer for the picked
   * cargo variant. Controls how many parallel cargo-item input rows
   * the create form renders for container / tank families.
   */
  perTrailer: number;
  /** Org-wide address pool — feeds the location combobox. */
  addresses: Address[];
  /**
   * Owning organisation — forwarded to the booking sub-form so the
   * Route picker can resolve per-org carrier/route selections. Nullable
   * while the page is still resolving the active org from Auth0.
   */
  organisationId: string | null;
  /**
   * Service-tag ids active on the order (operator-toggled from the
   * order header). Surfaced as read-only chips on the create form so
   * the operator sees which tags this task will inherit — changing
   * the set is an order-header action, not per-task.
   */
  serviceTagIds: string[];
  /**
   * Catalogue of every service tag the org has configured. Used to
   * resolve <c>serviceTagIds</c> into operator-facing labels for the
   * read-only chip row.
   */
  serviceTagsCatalogue: ServiceTag[];
  /**
   * Highest sequence value across the existing task list. New tasks
   * are appended at the end, so the form posts <c>maxSequence + 1</c>.
   * Pass 0 when the order has no tasks yet.
   */
  maxSequence: number;
  /** Planned date of the order's last existing task, or null when there are none. */
  previousPlannedDate: string | null;
  /** Exact planned time (HH:mm) of the last existing task, or null (no task / not Exact). */
  previousPlannedTime: string | null;
  /** Collected cargo items in the order — the pool a delivery leg allocates from. */
  collectionItems: CollectionItemOption[];
  /** Quantity already delivered per collection item id (across all delivery legs). */
  deliveredByCollectionItem: Record<string, number>;
  /** Fired on successful create — the parent typically closes the form. */
  onSaved: () => void;
  onCancel: () => void;
}

// ── Cargo-item draft model ──────────────────────────────────────────────────






/**
 * Inline create-task panel that appears below the Tasks header when
 * the operator clicks "+ Add". Mirrors the editor pattern used for
 * documents and references: small bordered card, FormSection-shaped
 * inputs, Cancel / Save action bar at the bottom.
 *
 * <p>
 * Type options are filtered against the picked cargo's allowed list
 * (see <c>CARGO_TASK_TYPES</c>) with the `Booking` synthetic entry
 * removed — bookings are created via their own flow which atomically
 * spawns the metadata row + both paired task halves. Specialty types
 * still in the cargo's list but not yet in the BE
 * <c>TaskTypeValue</c> union (e.g. <c>PalletExchange</c>) are
 * filtered out by the label-map presence check.
 * </p>
 */
export function TaskCreateForm({
  orderId,
  cargoType,
  cargoFamily,
  palletVariants,
  productOptions,
  multiProduct,
  temperatureSpec,
  defaultTemperatureC,
  defaultUnitIdentifiers,
  perTrailer,
  addresses,
  organisationId,
  serviceTagIds,
  serviceTagsCatalogue,
  maxSequence,
  previousPlannedDate,
  previousPlannedTime,
  collectionItems,
  deliveredByCollectionItem,
  onSaved,
  onCancel,
  orderNumber = null,
  dangerousGoods = false,
  equipmentKeys = [],
}: TaskCreateFormProps) {
  const createMutation = useCreateOrderTask();
  const createCargoItemMutation = useCreateOrderTaskCargoItem();
  const createBookingMutation = useCreateOrderBooking();
  const ferryFlow = useFerryBookingFlow();
  // The Integration switch can be On only while the organisation holds an
  // active account at the picked crossing's carrier: it starts On whenever
  // that becomes true, and is Off and grey otherwise.
  const { enabled: integrationEnabled } = useShippingIntegration(organisationId);
  const ferryCredentialsQuery = useFerryCredentials(integrationEnabled ? organisationId : null);
  const isBusy =
    createMutation.isPending
    || createCargoItemMutation.isPending
    || createBookingMutation.isPending
    || ferryFlow.isBusy;

  // Available task types for this cargo. Built once per cargo / locale
  // combination via the catalogue map + the BE-union check (drops
  // entries the BE doesn't know yet so the POST can't 422). Typed
  // as the object form of `SelectInputOption` (which is `string | {...}`)
  // so downstream `.value` reads type-check straight.
  //
  // `Booking` is the catalogue's synthetic entry standing in for "a
  // ferry crossing" — it doesn't map to a single `TaskTypeValue` on
  // the BE (the BE has the two paired halves `BookingDeparture` /
  // `BookingArrival`). It's kept in the dropdown here; the form
  // detects the synthetic value and switches into the booking
  // sub-form which calls `useCreateOrderBooking` (atomic
  // booking + two-tasks create) instead of `useCreateOrderTask`.
  const typeOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    const allowed = CARGO_TASK_TYPES[cargoType] ?? [];
    const out: Array<{ value: string; label: string }> = [];
    for (const t of allowed) {
      if (t === 'Booking') {
        out.push({ value: 'Booking', label: 'Booking' });
        continue;
      }
      const label = TASK_TYPE_LABELS_BE[t as TaskTypeValue];
      if (!label) continue;
      out.push({ value: t, label });
    }
    return out;
  }, [cargoType]);

  // Location combobox options. `name` is the operator-typed friendly
  // label; appended `, City` disambiguates two locations with the
  // same name in different towns.
  const locationOptions = useMemo<ComboboxOption[]>(() => {
    return addresses.map((a) => ({
      value: a.id,
      label: a.city ? `${a.name} — ${a.city}` : a.name,
    }));
  }, [addresses]);

  // Form state. Initial type defaults to the FIRST option so the
  // operator doesn't have to scroll to see a sensible starting
  // point on cargoes with a wide menu. The synthetic `'Booking'`
  // option is part of this union so the form can detect it and
  // switch into the booking sub-form layout.
  type CreateTaskTypeValue = TaskTypeValue | 'Booking';
  const [type, setType] = useState<CreateTaskTypeValue>(
    (typeOptions[0]?.value as CreateTaskTypeValue) ?? 'PickUp',
  );
  const isBooking = type === 'Booking';
  const [addressId, setAddressId] = useState<string>('');
  const [taskReference, setTaskReference] = useState<string>('');
  // Per-task target temperature; only surfaced for temperature-controlled
  // cargo (temperatureSpec != null). Validated against the cargo range
  // on save, same as the order-level box.
  //
  // Seeded from the ORDER's temperature: a temperature-controlled order almost
  // always wants the same set point at every stop, and retyping it per task is
  // both tedious and a chance to fat-finger a cold chain. Overwritable — the
  // seed is only the initial value, so a leg that genuinely differs still can.
  const [temperatureC, setTemperatureC] = useState<number | null>(
    defaultTemperatureC ?? null,
  );
  // Default the date to the order's last task (operator can push it out later),
  // otherwise today.
  const [plannedDate, setPlannedDate] = useState<string>(
    () => previousPlannedDate || todayIso(),
  );
  const [plannedTime, setPlannedTime] = useState<string>('');
  const [timeMode, setTimeMode] = useState<TaskTimeModeValue>('Any');
  const [notes, setNotes] = useState<string>('');

  // ── Booking sub-form state ─────────────────────────────────────────
  //
  // One blob backing the shared `BookingFormFields` component —
  // sail date/time, the two port addressIds, booking type, carrier
  // ref, vessel, voyage, accompaniment, drivers, vehicle reg,
  // trailer, customs ref, clearance status, notes. Defaults to
  // `Container40to49` booking type for container cargo (40ft is
  // the most common slot category), `Artic` otherwise.
  //
  // Stored as a single object so a Type → Booking → other → Booking
  // round-trip keeps the operator's typing intact — and so the
  // shared form component can take a single `values + onChange`
  // pair instead of 15 individual prop pairs.
  const [bookingValues, setBookingValues] = useState<BookingFormValues>({
    ...EMPTY_BOOKING_FORM_VALUES,
    bookingType: cargoFamily === 'container' ? 'Container40to49' : 'Artic',
  });
  const onBookingChange = (patch: Partial<BookingFormValues>) =>
    setBookingValues((prev) => ({ ...prev, ...patch }));
  const pickedFerryCarrier = bookingValues.ferryCarrierCode;
  const canIntegrate =
    integrationEnabled
    && !!pickedFerryCarrier
    && (ferryCredentialsQuery.data ?? []).some(
      (c) => c.status === 'Active' && c.carrierCode?.toUpperCase() === pickedFerryCarrier.toUpperCase(),
    );
  // Re-seeded on every carrier change, so a click stands only until the pick moves.
  useEffect(() => {
    setBookingValues((prev) =>
      prev.ferryBook === canIntegrate ? prev : { ...prev, ferryBook: canIntegrate },
    );
  }, [canIntegrate, pickedFerryCarrier]);

  // Cargo-item drafts. Container / tank tasks at 2PT carry two
  // parallel drafts (itemIndex 0 / 1); pallet tasks carry one. The
  // draft set is resized whenever family or perTrailer changes —
  // existing values for slots that survive the resize are kept,
  // dropped slots' values are discarded.
  const cargoItemKind = useMemo(() => cargoItemKindFor(cargoFamily), [cargoFamily]);
  // `Booking` isn't in the BE TaskTypeValue union — the conditional
  // render below short-circuits on `isBooking` first; this cast is just
  // to keep the type-check happy against the `TaskTypeValue` API.
  //
  // Container / tank own a cargo row on every task type (the barrel being
  // washed still has to be named); pallet-family cargo keeps to the
  // handling legs. See `taskTypeOwnsCargoItems`.
  const ownsCargoItems =
    type !== 'Booking' && taskTypeOwnsCargoItems(cargoFamily, type as TaskTypeValue);
  const isUnitFamily = UNIT_IDENTIFIER_FAMILIES.has(cargoFamily);
  // Delivery legs are allocation-driven (draw from the order's collections)
  // rather than free cargo entry.
  const isDelivery =
    type !== 'Booking' && cargoHandlingPhaseFor(type as TaskTypeValue) === 'delivery';
  // Default a new delivery to fully delivered — pre-fill each collected item's
  // remaining quantity (the 95% case). Operator can dial any row down.
  const [allocations, setAllocations] = useState<AllocationMap>(() => {
    const init: AllocationMap = {};
    for (const c of collectionItems) {
      const avail = Math.max(0, c.collectedQty - (deliveredByCollectionItem[c.id] ?? 0));
      if (avail > 0) init[c.id] = avail;
    }
    return init;
  });
  const availableById = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const c of collectionItems) {
      out[c.id] = Math.max(0, c.collectedQty - (deliveredByCollectionItem[c.id] ?? 0));
    }
    return out;
  }, [collectionItems, deliveredByCollectionItem]);
  // Reference shows for every non-booking type except the process events
  // (Stop / Instruction / Inspection). Temperature shows only when the
  // cargo type is temperature-controlled.
  const showReference = !isBooking && taskTypeSupportsReference(type as TaskTypeValue);
  const showTaskTemperature = !isBooking && temperatureSpec != null;
  const cargoItemRowCount = useMemo(
    () => cargoItemRowCountFor(cargoFamily, perTrailer),
    [cargoFamily, perTrailer],
  );
  const [cargoItems, setCargoItems] = useState<CargoItemDraft[]>(() =>
    Array.from({ length: cargoItemRowCount }, (_, i) => ({
      ...EMPTY_CARGO_ITEM_DRAFT,
      // Seeded, not locked — the operator overwrites it when the leg
      // genuinely carries a different unit.
      identifier: defaultUnitIdentifiers?.get(i + 1) ?? '',
    })),
  );
  // Keep `cargoItems.length` aligned with `cargoItemRowCount` —
  // growing pads with empties, shrinking truncates. Doesn't fire
  // when the count's already right so existing operator edits in
  // surviving slots aren't disturbed.
  useEffect(() => {
    setCargoItems((prev) => {
      if (prev.length === cargoItemRowCount) return prev;
      if (prev.length < cargoItemRowCount) {
        return [
          ...prev,
          ...Array.from({ length: cargoItemRowCount - prev.length }, (_, k) => ({
            ...EMPTY_CARGO_ITEM_DRAFT,
            identifier: defaultUnitIdentifiers?.get(prev.length + k + 1) ?? '',
          })),
        ];
      }
      return prev.slice(0, cargoItemRowCount);
    });
  }, [cargoItemRowCount, defaultUnitIdentifiers]);

  // Per-task tag selection. Defaults to the order's active set (so
  // a task inherits the order's tags out of the gate) but the
  // operator can toggle individual tags on/off here — useful when a
  // specific leg needs a narrower or wider applicable tag list than
  // the order header carries. Persisting per-task selections to the
  // BE is pending the OrderTask schema gaining a `serviceTagIds`
  // column; until then the selection captured here doesn't survive
  // the save round-trip. The UI is wired so the day the column
  // lands, only the POST body has to change.
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    () => new Set(serviceTagIds),
  );
  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // If the cargo changes mid-form and our current type isn't valid
  // any more, snap back to the first available option so the
  // operator doesn't post a POST that 422s on the server.
  useEffect(() => {
    if (typeOptions.length === 0) return;
    if (!typeOptions.some((o) => o.value === type)) {
      setType(typeOptions[0]?.value as CreateTaskTypeValue);
    }
  }, [typeOptions, type]);

  // Booking save needs both ports + sail date + booking type. Single-
  // task save needs an addressId. Both branches share `!isBusy`.
  const canSave = isBooking
    ? !!bookingValues.departureAddressId
      && !!bookingValues.arrivalAddressId
      && !!bookingValues.sailDate
      && !!bookingValues.bookingType
      && !isBusy
    : !!type && !!addressId && !isBusy;

  const handleCargoItemChange = (index: number, patch: Partial<CargoItemDraft>) => {
    setCargoItems((prev) => {
      const next = prev.slice();
      const current = next[index] ?? { ...EMPTY_CARGO_ITEM_DRAFT };
      next[index] = { ...current, ...patch };
      return next;
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    // ── Booking branch ─────────────────────────────────────────────
    // The BE creates the booking row + both paired tasks
    // (BookingDeparture + BookingArrival) in one transaction, so
    // there are no cargo items / tags to wire here — those land via
    // the booking edit panel post-create.
    if (isBooking) {
      // A Ferry-linked crossing is checked before anything is created, so a
      // missing vehicle type never leaves a half-made booking to undo.
      const blocking = ferryBlockingMessage(bookingValues);
      if (blocking) {
        showToast({ type: 'error', message: blocking });
        return;
      }
      try {
        const created = await createBookingMutation.mutateAsync({
          orderId,
          bookingType: bookingValues.bookingType,
          isAccompanied: bookingValues.isAccompanied,
          sailDate: bookingValues.sailDate || null,
          sailTime: bookingValues.sailTime || null,
          // Picked from the Carrier combobox; empty string → null so
          // the BE doesn't try to FK on an empty Guid.
          carrierPartyId: bookingValues.carrierPartyId || null,
          // Same empty-string → null collapse for the optional Agent.
          agentPartyId: bookingValues.agentPartyId || null,
          // Route picked from the catalogue combobox: carrierRouteId is
          // the OrganisationRoute.id; routeCode / routeName / typical
          // durations are the denormalised snapshot taken at pick time.
          carrierRouteId: bookingValues.carrierRouteId || null,
          routeCode: bookingValues.routeCode.trim() || null,
          routeName: bookingValues.routeName.trim() || null,
          typicalDurationMinutes: bookingValues.typicalDurationMinutes,
          typicalCheckInMinutes: bookingValues.typicalCheckInMinutes,
          typicalDriveOffMinutes: bookingValues.typicalDriveOffMinutes,
          carrierReference: bookingValues.carrierReference.trim() || null,
          vessel: bookingValues.vessel.trim() || null,
          voyageNumber: bookingValues.voyageNumber.trim() || null,
          numberOfDrivers: bookingValues.isAccompanied
            ? Number.parseInt(bookingValues.numberOfDrivers, 10) || null
            : null,
          vehicleRegistration: bookingValues.isAccompanied
            ? bookingValues.vehicleRegistration.trim() || null
            : null,
          trailerNumber: bookingValues.trailerNumber.trim() || null,
          customsReference: bookingValues.customsReference.trim() || null,
          clearanceStatus:
            bookingValues.clearanceStatus === '' ? null : bookingValues.clearanceStatus,
          notes: bookingValues.notes.trim() || null,
          departure: {
            addressId: bookingValues.departureAddressId,
            // Append at the end of the task list. The two paired
            // tasks share consecutive sequence numbers so the
            // crossing reads as one block in the timeline.
            sequence: maxSequence + 1,
            // Truck checks in on the sail date; specific check-in
            // time refined via the per-task edit panel later.
            plannedDate: bookingValues.sailDate || null,
            plannedTime: bookingValues.sailTime || null,
          },
          arrival: {
            addressId: bookingValues.arrivalAddressId,
            sequence: maxSequence + 2,
            // Arrival date defaults to sail date — most crossings
            // are same-day. Operator edits for overnight sailings
            // via the per-task edit panel.
            plannedDate: bookingValues.sailDate || null,
            plannedTime: null,
          },
        });

        // Then the carrier, when the crossing is booked through My.Ferry.
        if (bookingValues.ferryRouteCode && bookingValues.ferryCarrierId) {
          const sent = await ferryFlow.sendToFerry({
            orderId,
            tmsBooking: created,
            values: bookingValues,
            orderNumber,
          });
          if (sent.outcome === 'failed') {
            // The TMS booking was undone; the form stays open to try again.
            showToast({ type: 'error', message: sent.message });
            return;
          }
          if (sent.outcome === 'unknown') {
            // The TMS booking stays; the operator reads the carrier's answer from it.
            showToast({ type: 'warning', message: sent.message });
            onSaved();
            return;
          }
          if (sent.outcome === 'refused') {
            showToast({ type: 'error', message: `The carrier refused the booking: ${sent.message}` });
          } else {
            showToast({
              type: 'success',
              message: `Booked with the carrier, reference ${sent.booking.carrierBookingNumber}.`,
            });
          }
          onSaved();
          return;
        }

        showToast({ type: 'success', message: 'Booking added.' });
        onSaved();
      } catch (err) {
        showToast({
          type: 'error',
          message: err instanceof Error ? err.message : 'Failed to add booking.',
        });
      }
      return;
    }
    // Temperature range guard — only when the box is shown. Mirrors the
    // order-level violation toast so the operator can't save an
    // out-of-spec reading.
    if (showTaskTemperature) {
      const violation = temperatureRangeViolation(
        temperatureC,
        temperatureSpec?.minTempC,
        temperatureSpec?.maxTempC,
      );
      if (violation) {
        showToast({ type: 'error', message: violation });
        return;
      }
    }
    try {
      // Step 1: create the task. Carries no cargo-item data — the BE
      // doesn't have an atomic "task + items" endpoint, so the
      // post-create cargo POSTs run as separate calls below.
      const task = await createMutation.mutateAsync({
        orderId,
        // Append at the end. Sequence isn't guaranteed contiguous on
        // the BE (gaps are fine) but +1 keeps the list dense.
        sequence: maxSequence + 1,
        type: type as TaskTypeValue,
        addressId,
        // Per-stop reference — omitted for the process-event types that
        // don't carry one (Stop / Instruction / Inspection).
        taskReference: showReference ? taskReference.trim() || null : null,
        // Per-task temperature — only for temperature-controlled cargo.
        temperatureC: showTaskTemperature ? temperatureC : null,
        plannedDate: plannedDate || null,
        // Only persist a specific time when the operator picked
        // Exact — Any / AM / PM are mode-only signals.
        plannedTime: timeMode === 'Exact' ? plannedTime || null : null,
        timeMode,
        notes: notes.trim() || null,
        // Per-task tag selection. Seeded from the order's active
        // set when the form opened; operator may have toggled some
        // on/off in the chip row.
        serviceTagIds: Array.from(selectedTagIds),
        // `state` defaults to Pending server-side. statusText,
        // plannedDepartureTime aren't exposed in this form.
      });

      // Step 2: cargo items. Pallet-family cargo only on the handling legs;
      // container / tank on every type. For anything else we're done after
      // the task POST.
      if (ownsCargoItems) {
        let itemErrors = 0;
        // Delivery legs: POST one cargo item per allocated collection source
        // (commodity copied, LM/weight pro-rated). The `!isDelivery` guard on
        // the draft loop below skips free-entry for deliveries.
        if (isDelivery) {
          const collById = new Map(collectionItems.map((c) => [c.id, c]));
          const slotOfUnit = (unit: number) =>
            cargoItemRowCount <= 1 ? 1 : Math.min(Math.max(unit, 1), cargoItemRowCount);
          const usedIndexes = new Set<number>();
          // A unit nothing is allocated off yet still has the Tank ID / BIC
          // typed above the allocation table. It gets a unit-only row numbered
          // by its unit — created first so it can take that index — which is
          // what the edit form reads back and later allocations attach to.
          if (isUnitFamily) {
            const coveredSlots = new Set(
              Object.entries(allocations)
                .filter(([collId, qty]) => qty > 0 && collById.has(collId))
                .map(([collId]) => slotOfUnit(collById.get(collId)!.unitIndex)),
            );
            for (let slot = 1; slot <= cargoItemRowCount; slot++) {
              if (coveredSlots.has(slot)) continue;
              const unit = unitDraftForRow(cargoItems, cargoItemRowCount, slot);
              if (
                !unit ||
                !(
                  unit.identifier.trim() ||
                  unit.seal.trim() ||
                  unit.sealStatus !== '' ||
                  unit.tankStatus !== '' ||
                  unit.capacity != null
                )
              ) {
                continue;
              }
              usedIndexes.add(slot);
              try {
                await createCargoItemMutation.mutateAsync({
                  orderId,
                  body: {
                    orderTaskId: task.id,
                    itemIndex: slot,
                    kind: cargoItemKind,
                    identifier: unit.identifier.trim() || null,
                    seal: unit.seal.trim() || null,
                    sealStatus: unit.sealStatus !== '' ? unit.sealStatus : null,
                    tankStatus: unit.tankStatus !== '' ? unit.tankStatus : null,
                    capacity: unit.capacity ?? null,
                  },
                });
              } catch (err) {
                itemErrors += 1;
                console.error('Unit row create failed', err);
              }
            }
          }
          let itemIndex = 0;
          for (const [collId, qty] of Object.entries(allocations)) {
            if (qty <= 0) continue;
            const coll = collById.get(collId);
            if (!coll) continue;
            const d = deriveDeliveryItem(coll, qty);
            do itemIndex += 1;
            while (usedIndexes.has(itemIndex));
            // For a unit family the operator has also named the barrel above
            // the allocation table. Fold those fields into the same POST
            // rather than a second pass: a row created here and patched later
            // would be two round-trips, and a failure between them would
            // leave an allocation row with nobody's Tank ID on it.
            //
            // `weightKg` stays the ALLOCATION's pro-rated figure — on a
            // delivery that column is what came off this source, which is the
            // number the allocation knows and the unit row does not.
            // The tank / container this product came off — so two products
            // from one tank both carry that tank's ID.
            const unit = isUnitFamily
              ? unitDraftForRow(cargoItems, cargoItemRowCount, coll.unitIndex)
              : null;
            try {
              await createCargoItemMutation.mutateAsync({
                orderId,
                body: {
                  orderTaskId: task.id,
                  itemIndex,
                  kind: d.kind,
                  sourceCollectionItemId: d.sourceCollectionItemId,
                  sourceProductLineId: d.sourceProductLineId,
                  description: d.description,
                  quantity: d.quantity,
                  quantityUnit: d.quantityUnit,
                  loadingMetresM: d.loadingMetresM,
                  weightKg: d.weightKg,
                  volumeM3: d.volumeM3,
                  identifier: unit?.identifier.trim() || null,
                  seal: unit?.seal.trim() || null,
                  sealStatus: unit && unit.sealStatus !== '' ? unit.sealStatus : null,
                  tankStatus: unit && unit.tankStatus !== '' ? unit.tankStatus : null,
                  capacity: unit?.capacity ?? null,
                },
              });
            } catch (err) {
              itemErrors += 1;
              console.error('Allocation create failed', err);
            }
          }
        }
        for (let i = 0; !isDelivery && i < cargoItems.length; i++) {
          const draft = cargoItems[i] ?? EMPTY_CARGO_ITEM_DRAFT;
          // Always POST a row per `perTrailer` slot (or per pallet
          // line) for a cargo-handling task — the slot is part of
          // the task's conceptual shape, not something the
          // operator opts into. Empty fields land as `null` columns
          // on the BE and the operator fills them in via the edit
          // form when the data lands (driver pings BIC code,
          // weighbridge reports a number, etc.).
          try {
            await createCargoItemMutation.mutateAsync({
              orderId,
              body: {
                orderTaskId: task.id,
                // BE `itemIndex` is 1-based (validator: > 0). The
                // form's loop variable `i` is the JS array index
                // (0-based) so the wire value is `i + 1`.
                itemIndex: i + 1,
                kind: cargoItemKind,
                identifier: draft.identifier.trim() || null,
                seal: draft.seal.trim() || null,
                sealStatus: draft.sealStatus === '' ? null : draft.sealStatus,
                tankStatus: draft.tankStatus === '' ? null : draft.tankStatus,
                capacity: draft.capacity,
                weightKg: draft.weightKg,
                volumeM3: draft.volumeM3,
                loadingMetresM: resolveLoadingMetres(draft, palletVariants),
                description: draft.description.trim() || null,
                quantity: draft.quantity,
                // Quantity unit defaults to 'pallet' for pallet
                // family; left null for container/tank (they don't
                // use the quantity field at all).
                quantityUnit: cargoFamily === 'pallet' && draft.quantity != null ? 'pallet' : null,
                products: toProductInputs(draft.products),
              },
            });
          } catch (err) {
            itemErrors += 1;
            // Surface the per-row failure but keep going — the task
            // itself is already saved, partial cargo items are
            // better than rolling back the entire create.
            console.error('Cargo item create failed', err);
          }
        }
        if (itemErrors > 0) {
          showToast({
            type: 'error',
            message:
              itemErrors === 1
                ? 'Task saved, but one cargo row failed to attach.'
                : `Task saved, but ${itemErrors} cargo rows failed to attach.`,
          });
          // Don't close the form on partial failure — operator can
          // see which rows still need attention.
          return;
        }
      }

      showToast({ type: 'success', message: 'Task added.' });
      onSaved();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to add task.',
      });
    }
  };

  // Same display-vocab bridge as `PickupDeliveryForm`. Pill picker
  // speaks `Undefined` ↔ wire `Any`.
  const displayTimeMode: TimeMode = timeMode === 'Any' ? 'Undefined' : timeMode;
  const setDisplayTimeMode = (next: TimeMode) => {
    const mode = next === 'Undefined' ? 'Any' : next;
    setTimeMode(mode);
    // Seed a sensible exact time when the operator switches to Exact and none is
    // set yet (previous task + 30m, else next quarter-hour today / 08:00).
    if (mode === 'Exact' && !plannedTime) {
      setPlannedTime(defaultExactTime(previousPlannedTime, plannedDate));
    }
  };

  // Resolve the currently-picked location for the single-task map.
  // In booking mode the BookingFormFields component owns its own
  // map (showing both ports + the polyline), so this only resolves
  // the single-task address; booking mode falls back to null and
  // the outer layout hides the TaskLocationMap entirely.
  const selectedAddress = useMemo(
    () => (isBooking ? null : (addresses.find((a) => a.id === addressId) ?? null)),
    [addresses, addressId, isBooking],
  );

  return (
    // Outer two-pane grid in non-booking mode: the "Add task" white
    // box on the left, the single-pin map on the right. In booking
    // mode the inner `BookingFormFields` carries its own two-pane
    // layout (form + dual-pin map), so the outer grid collapses to
    // a single full-width column to avoid nesting two map panes.
    <div
      className={`m-3 grid grid-cols-1 gap-3 ${isBooking ? '' : 'lg:grid-cols-[3fr_2fr]'}`}
    >
      <div className="space-y-3 rounded-md border border-purple-200 bg-purple-50/40 p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Add task</h3>
          {/* Same pill as the Bookings cockpit: a booking task can be sent to
              the carrier through My.Ferry (On) or stay a TMS booking (Off).
              Not shown at all while the organisation's integration is off. */}
          {isBooking && integrationEnabled && (
            <IntegrationSwitch
              on={bookingValues.ferryBook}
              disabled={isBusy || !canIntegrate}
              onToggle={() => onBookingChange({ ferryBook: !bookingValues.ferryBook })}
            />
          )}
        </div>

        {/* Row 1 — Type dropdown. Always rendered; subsequent rows
            branch on whether the picked type is the synthetic
            `Booking` (port-pair sub-form) or any other value
            (single-task fields). */}
        {!isBooking && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SelectInput
              label="Type *"
              name="new-task-type"
              value={type}
              options={typeOptions}
              disabled={isBusy || typeOptions.length === 0}
              onChange={(e) => setType(e.target.value as CreateTaskTypeValue)}
            />
            <div className="grid grid-cols-2 gap-2">
              <div className={timeMode === 'Exact' ? '' : 'col-span-2'}>
                <TextInput
                  label="Planned date"
                  type="date"
                  value={plannedDate}
                  disabled={isBusy}
                  onChange={(e) => setPlannedDate(e.target.value)}
                />
              </div>
              {timeMode === 'Exact' && (
                <QuarterHourTimeInput
                  label="Planned time"
                  value={plannedTime}
                  disabled={isBusy}
                  onChange={setPlannedTime}
                />
              )}
            </div>
            <div className="flex min-h-9 flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                Time mode
              </span>
              <TimeModePicker value={displayTimeMode} onChange={setDisplayTimeMode} />
            </div>
          </div>
        )}

        {/* Single-task location row — replaced by the booking
            sub-form below when type === Booking. */}
        {!isBooking && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {/* "+" makes a Location on the spot and picks it — the Locations
                list refreshes on create, so the new one appears as an option
                by itself. */}
            <div className="flex items-start gap-1.5">
              <div className="min-w-0 flex-1">
                <Combobox
                  label="Location *"
                  value={addressId}
                  options={locationOptions}
                  onChange={setAddressId}
                  placeholder={
                    locationOptions.length === 0
                      ? 'No locations yet — add one with +'
                      : 'Start typing a location or city…'
                  }
                  allowClear
                />
              </div>
              <NewLocationButton
                organisationId={organisationId}
                onCreated={setAddressId}
                disabled={isBusy}
              />
            </div>
            <div className="flex min-h-9 items-center">
              {selectedAddress?.fullAddress ? (
                <p
                  className="text-sm italic leading-snug text-slate-600"
                  title={selectedAddress.fullAddress}
                >
                  {selectedAddress.fullAddress}
                </p>
              ) : (
                <p className="text-xs italic text-slate-300">
                  Address shows here once a location is picked
                </p>
              )}
            </div>
          </div>
        )}

        {/* Reference + per-task temperature. Reference hides for the
            process-event types (Stop / Instruction / Inspection);
            temperature only shows for temperature-controlled cargo.
            Sit beside each other in a shared 2-col row when both apply. */}
        {(showReference || showTaskTemperature) && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {showReference && (
              <TextInput
                label="Reference"
                name="new-task-reference"
                autoComplete="off"
                value={taskReference}
                disabled={isBusy}
                maxLength={64}
                onChange={(e) => setTaskReference(e.target.value)}
              />
            )}
            {showTaskTemperature && (
              <InputWithSpecHint
                spec={formatTempSpecRange(
                  temperatureSpec?.minTempC,
                  temperatureSpec?.maxTempC,
                  temperatureSpec?.maxTransitDays,
                )}
              >
                <NumberFieldInput
                  label="Temp (°C)"
                  value={temperatureC}
                  disabled={isBusy}
                  onChange={setTemperatureC}
                  allowNegative
                />
              </InputWithSpecHint>
            )}
          </div>
        )}

        {/* ── Booking sub-form ──────────────────────────────────────
            Uses the shared `BookingFormFields` so create + edit
            render an identical layout. The Type dropdown lives
            above this block (only shown in create — edit already
            knows it's a booking). Vessel / voyage / customs /
            accompaniment / drivers / vehicle / trailer / clearance
            / notes are all editable here, matching the edit panel.
            The right-hand pane carries the BookingMap (two pins +
            polyline) so the operator sees the crossing route as
            they pick the ports. */}
        {isBooking && (
          <div className="space-y-3">
            {/* Type dropdown sits on top of the form pane only —
                matches the column widths of the inner
                `BookingFormFields` grid below (3fr form + 2fr map).
                The right side stays empty so the Type select doesn't
                stretch under the map area, and the Type input width
                matches a single cell of the 3-col row right beneath. */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <SelectInput
                  label="Type *"
                  name="new-task-type"
                  value={type}
                  options={typeOptions}
                  disabled={isBusy || typeOptions.length === 0}
                  onChange={(e) => setType(e.target.value as CreateTaskTypeValue)}
                />
                <div />
                <div />
              </div>
              <div />
            </div>
            <BookingFormFields
              values={bookingValues}
              onChange={onBookingChange}
              addresses={addresses}
              organisationId={organisationId}
              disabled={isBusy}
              mode="create"
              // The order's load, for the carrier booking's defaults.
              cargo={{
                temperatureControlled: temperatureSpec !== null,
                temperatureC: defaultTemperatureC ?? null,
                dangerousGoods,
                equipmentKeys,
              }}
            />
          </div>
        )}

        {/* Cargo-item inputs. `perTrailer` is consumed indirectly via the
            draft array's length (`cargoItemRowCountFor(family, perTrailer)`
            decides how many rows to render — the section just maps over the
            drafts the parent gave it).

            Pallet-family cargo appears on the handling legs only; process
            events (Stop / Instruction / Inspection / …) have no quantity to
            record and hide the section. Container / tank appear on every
            type, because the unit is what the task is about.

            Booking spawns its own cargo-agnostic tasks through an endpoint
            that takes no cargo items, so it renders nothing here whatever
            the family — the pair picks its unit up on the task panel, or
            from the next cascade. */}
        {!isBooking && ownsCargoItems && (
          <>
            {(!isDelivery || isUnitFamily) && (
              <CargoItemInputsSection
                family={cargoFamily}
                taskType={type as TaskTypeValue}
                drafts={cargoItems}
                disabled={isBusy}
                palletVariants={palletVariants}
                productOptions={productOptions}
                multiProduct={multiProduct}
                onChange={handleCargoItemChange}
              />
            )}
            {isDelivery && (
              <AllocationTable
                items={collectionItems}
                availableById={availableById}
                value={allocations}
                onChange={setAllocations}
                disabled={isBusy}
              />
            )}
          </>
        )}

        {/* Service tag picker. Hidden in booking mode — the BE
            `CreateOrderBookingTaskInput` doesn't accept tag ids
            for the paired tasks, so anything set here couldn't
            persist. Operator adds tags via the per-task edit panel
            after the booking lands. */}
        {!isBooking && serviceTagsCatalogue.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Tags
            </span>
            {serviceTagsCatalogue.map((tag) => {
              const isActive = selectedTagIds.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(tag.id)}
                  disabled={isBusy}
                  aria-pressed={isActive}
                  title={
                    isActive
                      ? `Click to remove ${tag.name} from this task`
                      : `Click to add ${tag.name} to this task`
                  }
                  className={
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-50 ' +
                    (isActive
                      ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                      : 'border border-slate-300 bg-white text-slate-500 hover:border-orange-300 hover:text-orange-600')
                  }
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Notes always last — operator scans the structured inputs
            first (type / when / where / cargo / tags) and only then
            types any free-text annotation. Hidden in booking mode
            because `BookingFormFields` carries its own Notes input
            bound to `bookingValues.notes`. */}
        {!isBooking && (
          <TextAreaInput
            label="Notes"
            value={notes}
            disabled={isBusy}
            onChange={(e) => setNotes(e.target.value)}
          />
        )}

        {/* Cancel / Save action bar — inside the box, right-aligned,
            so the operator's final actions live with the form they
            apply to (rather than floating outside on the surrounding
            page background). */}
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Save task"
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Single-pin map preview for non-booking task types. Booking
          mode owns its own two-pin + polyline map inside
          `BookingFormFields`, so we omit the single-pin map here to
          avoid having two map panes side by side. */}
      {!isBooking && <TaskLocationMap selected={selectedAddress} />}
    </div>
  );
}
